import { useEffect, useRef, useState, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Loader2,
  AlertCircle,
  FileSignature,
  Move,
  Check,
  Sparkles,
} from 'lucide-react';
import { api } from '../services/api';

export interface SignaturePlacement {
  page: number;
  x: number; // percentage 0 - 100
  y: number; // percentage 0 - 100
  width: number; // percentage 0 - 100
  height: number; // percentage 0 - 100
}

interface InteractivePdfViewerProps {
  documentId: string;
  signerId?: string;
  initialPage?: number;
  signerName?: string;
  signaturePreview?: string | null;
  onPositionChange?: (pos: SignaturePlacement) => void;
  interactive?: boolean;
}

export default function InteractivePdfViewer({
  documentId,
  signerId,
  initialPage = 1,
  signerName,
  signaturePreview,
  onPositionChange,
  interactive = true,
}: InteractivePdfViewerProps) {
  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [numPages, setNumPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [scale, setScale] = useState<number>(1.2);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [autoDetected, setAutoDetected] = useState<boolean>(false);

  // Position in percentage (0 - 100)
  const [position, setPosition] = useState<SignaturePlacement>({
    page: initialPage,
    x: 58,
    y: 40,
    width: 28,
    height: 10,
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const renderTaskRef = useRef<any>(null);
  const isDraggingRef = useRef<boolean>(false);
  const dragStartRef = useRef<{ mouseX: number; mouseY: number; startX: number; startY: number }>({
    mouseX: 0,
    mouseY: 0,
    startX: 0,
    startY: 0,
  });

  // Load PDF file
  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);
      try {
        const endpoint = signerId
          ? `/signers/${signerId}/file`
          : `/documents/${documentId}/view`;

        const res = await api.get(endpoint, { responseType: 'blob' });
        if (cancelled) return;

        if (res.data.type === 'application/json') {
          const text = await res.data.text();
          throw new Error(JSON.parse(text)?.error?.message || 'Không thể tải PDF');
        }

        const arrayBuffer = await res.data.arrayBuffer();
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url
        ).toString();

        const doc = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        if (cancelled) return;

        setPdfDoc(doc);
        setNumPages(doc.numPages);

        // Smart search for signer's name across pages
        let matchedPage = initialPage;
        let matchedX = 58;
        let matchedY = 40;
        let found = false;

        if (signerName && interactive) {
          const target = signerName.trim().toLowerCase();
          for (let p = 1; p <= doc.numPages; p++) {
            const pageObj = await doc.getPage(p);
            const textContent = await pageObj.getTextContent();
            const textItems = textContent.items as Array<any>;
            const fullText = textItems.map((item) => item.str).join(' ');

            if (fullText.toLowerCase().includes(target)) {
              matchedPage = p;
              found = true;

              // Find exact coordinates of target item if possible
              const vp = pageObj.getViewport({ scale: 1 });
              const targetParts = target.split(/\s+/).filter(Boolean);
              const lastName = targetParts[targetParts.length - 1];
              const firstName = targetParts[0];

              let foundItem = textItems.find((item) => item.str.toLowerCase().includes(target));
              if (!foundItem && firstName) {
                foundItem = textItems.find((item) => item.str.toLowerCase().includes(firstName));
              }
              if (!foundItem && lastName) {
                foundItem = textItems.find((item) => item.str.toLowerCase().includes(lastName));
              }

              if (foundItem && foundItem.transform) {
                const pdfX = foundItem.transform[4];
                const pdfY = foundItem.transform[5];
                // In PDF.js viewport, y is from bottom-left
                const nameTopPercent = ((vp.height - pdfY) / vp.height) * 100;
                // Place signature box nicely in the blank space right above their printed name
                const boxH = 6.0;
                const boxW = 20;
                const relY = Math.max(5, Math.min(85, nameTopPercent - boxH - 0.5));
                const relX = Math.max(5, Math.min(75, (pdfX / vp.width) * 100 - 1));
                matchedX = Math.round(relX * 10) / 10;
                matchedY = Math.round(relY * 10) / 10;
              }
              break;
            }
          }
        }

        if (found) {
          setCurrentPage(matchedPage);
          const newPos: SignaturePlacement = {
            page: matchedPage,
            x: matchedX,
            y: matchedY,
            width: 20,
            height: 6.0,
          };
          setPosition(newPos);
          setAutoDetected(true);
          onPositionChange?.(newPos);
        } else {
          const defaultPage = Math.min(2, doc.numPages);
          setCurrentPage(defaultPage);
          const newPos: SignaturePlacement = {
            page: defaultPage,
            x: 60,
            y: 41.5,
            width: 20,
            height: 6.0,
          };
          setPosition(newPos);
          onPositionChange?.(newPos);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.error?.message || err.message || 'Không thể đọc tệp PDF');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [documentId, signerId, signerName, interactive]);

  // Render current page to Canvas
  const renderPage = useCallback(async () => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }

      const page = await pdfDoc.getPage(currentPage);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const viewport = page.getViewport({ scale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = {
        canvasContext: ctx,
        viewport,
      };

      const renderTask = page.render(renderContext);
      renderTaskRef.current = renderTask;
      await renderTask.promise;
    } catch (err: any) {
      if (err.name !== 'RenderingCancelledException') {
        console.error('Render error:', err);
      }
    }
  }, [pdfDoc, currentPage, scale]);

  useEffect(() => {
    renderPage();
  }, [renderPage]);

  // Update position and notify parent
  const updatePos = useCallback(
    (newPos: Partial<SignaturePlacement>) => {
      setPosition((prev) => {
        const next = { ...prev, ...newPos, page: currentPage };
        onPositionChange?.(next);
        return next;
      });
    },
    [currentPage, onPositionChange]
  );

  // Click on PDF overlay to place signature
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    const boxW = position.width;
    const boxH = position.height;

    // Center the signature on click point
    let relX = (clickX / rect.width) * 100 - boxW / 2;
    let relY = (clickY / rect.height) * 100 - boxH / 2;

    // Clamp inside container
    relX = Math.max(1, Math.min(100 - boxW - 1, relX));
    relY = Math.max(1, Math.min(100 - boxH - 1, relY));

    updatePos({ x: Math.round(relX * 10) / 10, y: Math.round(relY * 10) / 10 });
  };

  // Drag handlers for the signature stamp box
  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    isDraggingRef.current = true;
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      startX: position.x,
      startY: position.y,
    };

    const handleMouseMove = (moveEvt: MouseEvent) => {
      if (!isDraggingRef.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const deltaXPercent = ((moveEvt.clientX - dragStartRef.current.mouseX) / rect.width) * 100;
      const deltaYPercent = ((moveEvt.clientY - dragStartRef.current.mouseY) / rect.height) * 100;

      let nextX = dragStartRef.current.startX + deltaXPercent;
      let nextY = dragStartRef.current.startY + deltaYPercent;

      nextX = Math.max(1, Math.min(100 - position.width - 1, nextX));
      nextY = Math.max(1, Math.min(100 - position.height - 1, nextY));

      updatePos({ x: Math.round(nextX * 10) / 10, y: Math.round(nextY * 10) / 10 });
    };

    const handleMouseUp = () => {
      isDraggingRef.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const changePage = (newPage: number) => {
    const p = Math.max(1, Math.min(numPages, newPage));
    setCurrentPage(p);
    updatePos({ page: p });
  };

  return (
    <div className="flex flex-col space-y-3 w-full">
      {/* Viewer Toolbar */}
      <div className="flex items-center justify-between p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 shadow-sm flex-wrap gap-2 text-xs">
        {/* Page navigation */}
        <div className="flex items-center gap-1.5 font-mono">
          <button
            type="button"
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage <= 1 || loading}
            className="btn-ghost p-1.5 rounded-lg disabled:opacity-30"
            title="Trang trước"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <span className="text-xs text-slate-700 dark:text-slate-300 px-1">
            Trang <strong>{currentPage}</strong> / {numPages}
          </span>

          <button
            type="button"
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage >= numPages || loading}
            className="btn-ghost p-1.5 rounded-lg disabled:opacity-30"
            title="Trang sau"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Quick Page Jump Dropdown */}
          {numPages > 1 && (
            <select
              value={currentPage}
              onChange={(e) => changePage(Number(e.target.value))}
              className="select h-7 py-0 px-2 text-xs font-normal ml-1"
            >
              {Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
                <option key={p} value={p}>
                  Trang {p} {p === 2 ? '(Khu vực ký tên)' : p === numPages ? '(Phụ lục cuối)' : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Zoom & Helper actions */}
        <div className="flex items-center gap-2">
          {interactive && (
            <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-sky-600 dark:text-sky-400 bg-sky-50 dark:bg-sky-950/40 px-2.5 py-1 rounded-lg border border-sky-200 dark:border-sky-500/20 font-medium">
              <Move className="w-3.5 h-3.5 text-sky-500" />
              <span>Nhấp chuột lên trang để di chuyển ô ký</span>
            </div>
          )}

          <div className="flex items-center gap-1 border-l border-slate-200 dark:border-white/10 pl-2">
            <button
              type="button"
              onClick={() => setScale((s) => Math.max(0.7, s - 0.15))}
              className="btn-ghost p-1.5 rounded-lg text-slate-500 hover:text-slate-800 dark:hover:text-white"
              title="Thu nhỏ"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-[11px] font-mono text-slate-400 min-w-[36px] text-center">
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setScale((s) => Math.min(2.0, s + 0.15))}
              className="btn-ghost p-1.5 rounded-lg text-slate-500 hover:text-slate-800 dark:hover:text-white"
              title="Phóng to"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Smart auto-detection notification */}
      {autoDetected && interactive && (
        <div className="p-2.5 rounded-xl bg-gradient-to-r from-emerald-500/10 to-sky-500/10 border border-emerald-500/30 flex items-center justify-between text-xs text-emerald-700 dark:text-emerald-300 animate-fade-in">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span>
              <strong>Tự động nhận diện:</strong> Ô chữ ký đã được định vị trên{' '}
              <strong>Trang {position.page}</strong> (ngay trên tên <strong>{signerName}</strong>).
            </span>
          </div>
          <span className="text-[11px] opacity-80 hidden sm:inline">
            (Có thể nhấp hoặc kéo để tinh chỉnh)
          </span>
        </div>
      )}

      {/* Document Canvas Container with Interactive Placement Overlay */}
      <div className="relative bg-slate-200 dark:bg-slate-950/80 rounded-2xl overflow-auto border border-slate-200 dark:border-white/10 shadow-inner flex justify-center p-3 sm:p-6 min-h-[500px]">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-950/90 z-20">
            <div className="text-center">
              <Loader2 className="w-8 h-8 text-sky-500 animate-spin mx-auto mb-2" />
              <p className="text-xs text-slate-500 dark:text-slate-400">Đang hiển thị trang PDF...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-6 bg-white/90 dark:bg-slate-950/90 z-20">
            <div className="text-center max-w-sm">
              <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-3">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="btn-primary text-xs h-7 px-3"
              >
                Tải lại
              </button>
            </div>
          </div>
        )}

        {/* Scaled PDF Page Wrapper */}
        <div
          ref={containerRef}
          onClick={handleOverlayClick}
          className="relative select-none shadow-xl rounded-sm transition-transform cursor-crosshair bg-white"
          style={{
            maxWidth: '100%',
          }}
        >
          {/* Canvas for crisp PDF rendering */}
          <canvas ref={canvasRef} className="block w-full h-auto rounded-sm pointer-events-none" />

          {/* Interactive Signature Box Overlay (Only visible on the designated page) */}
          {interactive && position.page === currentPage && (
            <div
              style={{
                left: `${position.x}%`,
                top: `${position.y}%`,
                width: `${position.width}%`,
                height: `${position.height}%`,
              }}
              onMouseDown={handleMouseDown}
              className="absolute z-10 border-2 border-dashed border-sky-500 bg-sky-500/15 rounded-lg shadow-lg flex flex-col items-center justify-center cursor-move hover:border-sky-400 hover:bg-sky-500/20 transition-colors group"
            >
              {/* Header Label / Move Indicator */}
              <div className="absolute -top-6 left-0 bg-sky-600 text-white text-[10px] font-medium px-2 py-0.5 rounded shadow-sm flex items-center gap-1 whitespace-nowrap pointer-events-none">
                <FileSignature className="w-3 h-3" />
                <span>Chữ ký ({signerName || 'Bạn'})</span>
              </div>

              {/* Signature Preview Content */}
              {signaturePreview ? (
                <img
                  src={signaturePreview}
                  alt="Chữ ký"
                  className="max-h-full max-w-full object-contain pointer-events-none select-none p-1"
                />
              ) : (
                <div className="text-center pointer-events-none p-1">
                  <p className="text-[11px] font-semibold text-sky-800 dark:text-sky-200">
                    Vị trí đóng dấu chữ ký
                  </p>
                  <p className="text-[9px] text-sky-600 dark:text-sky-300">
                    (Nhấp hoặc kéo để chỉnh)
                  </p>
                </div>
              )}

              {/* Position Coordinates indicator */}
              <div className="absolute -bottom-5 right-0 bg-slate-900/80 text-white text-[9px] font-mono px-1.5 py-0.5 rounded shadow-sm pointer-events-none">
                x: {position.x.toFixed(0)}%, y: {position.y.toFixed(0)}%
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
